import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { toast } from '@/hooks/use-toast';
import { useDemoMode } from '@/hooks/use-demo-mode';
import { activationStateAtom, DestinationsState } from '@/states';
import { trackEvent } from '@/helpers/external-tools-helper';
import { ACTIVATION_ANALYTICS_EVENTS } from '@/analytics/activation';
import { ClientContainer, UserContainer, ReportsContainer } from '@/providers';
import { ConnectionQuery } from '@/queries/connection';
import { useSession } from '@/states/common';
import { APP_ROUTE } from '@/constants';
import { axios } from '@/api/axios';
import { useComputedActivationState } from './use-computed-activation-state';

export const useActivationCompletion = () => {
  const { t } = useTranslation('activation');
  const navigate = useNavigate();
  const [quickStart, setQuickStart] = useAtom(activationStateAtom);
  const destination = useRecoilValue(DestinationsState);
  const { state: clientState } = ClientContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();
  const { state: reportState } = ReportsContainer.useContainer();
  const { data } = useSession();
  const { isDemoMode } = useDemoMode();

  const { data: providerAccounts } = ConnectionQuery.useDataSources(
    data.business?.id
  );

  const { computeState } = useComputedActivationState();

  const isActivationFlow = userState.business?.activationFlow === false;
  const isDestinations = Object.keys(destination).length > 0;

  const checkQuickSteps = useCallback(() => {
    const { datasources, destination: quickDestination, report } = quickStart;

    // 1. Check datasource status
    const connectedAccounts =
      providerAccounts?.filter?.((acc: any) => acc?.clientId) ?? [];

    if (connectedAccounts.length === 0 && datasources === 'completed') {
      // Reset to pending if no connections found but marked as completed
      setQuickStart(prevStart => ({
        ...prevStart,
        datasources: 'pending',
      }));
    }

    if (connectedAccounts.length > 0 && datasources === 'pending') {
      setTimeout(() => {
        setQuickStart(prevStart => ({
          ...prevStart,
          datasources: 'completed',
        }));
      }, 3000);
    }

    // 2. Check destination status
    if (!isDestinations && quickDestination === 'completed') {
      setQuickStart(prevState => ({
        ...prevState,
        destination: 'pending',
      }));
    }

    if (isDestinations && quickDestination === 'pending') {
      setQuickStart(prevState => ({
        ...prevState,
        destination: 'completed',
      }));
    }

    // 3. Check report status
    if (reportState.reports.length > 0 && report === 'pending') {
      setQuickStart(prevState => ({
        ...prevState,
        report: 'completed',
      }));
    }

    if (reportState.reports.length === 0 && report === 'completed') {
      setQuickStart(prevState => ({
        ...prevState,
        report: 'pending',
      }));
    }
  }, [
    quickStart,
    providerAccounts,
    isDestinations,
    reportState.reports.length,
    setQuickStart,
  ]);

  // Initialize state from localStorage or compute from actual data
  // Note: atomWithStorage automatically loads from localStorage on mount
  useEffect(() => {
    // In demo mode, mark all steps as completed
    if (isDemoMode) {
      setQuickStart({
        account: 'completed',
        datasources: 'completed',
        destination: 'completed',
        report: 'completed',
      });
      return;
    }

    // If activation flow is already complete, don't show it
    if (!isActivationFlow) {
      return;
    }

    // Check if state looks uninitialized (all pending except account)
    // If so, compute from actual data as a fallback
    if (
      quickStart.datasources === 'pending' &&
      quickStart.destination === 'pending' &&
      quickStart.report === 'pending'
    ) {
      const computed = computeState();
      // Only update if computed state differs (has some completed items)
      const hasProgress =
        computed.datasources === 'completed' ||
        computed.destination === 'completed' ||
        computed.report === 'completed';
      if (hasProgress) {
        setQuickStart(computed);
      }
    }
  }, [isDemoMode, isActivationFlow, computeState, setQuickStart, quickStart]);

  // Run automatic detection to sync with actual data
  useEffect(() => {
    if (isDemoMode || !isActivationFlow) return;
    checkQuickSteps();
  }, [isDemoMode, isActivationFlow, checkQuickSteps]);

  const completeDataSourceStep = () => {
    if (!isActivationFlow) return;

    // Update state (atomWithStorage automatically saves to localStorage)
    setQuickStart({ ...quickStart, datasources: 'completed' });

    // Show completion toast
    toast({
      variant: 'success',
      title: t('step_2_completed', 'Data source connected! 🎉'),
      description: t(
        'step_2_description',
        'Your data is now syncing. Next step: Connect Google Drive'
      ),
    });

    // Track completion
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_ADD_DATASOURCE_COMPLETED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );

    // Navigation to analytics happens in the calling component
  };

  const completeDestinationStep = () => {
    if (!isActivationFlow) return;

    // Update state (atomWithStorage automatically saves to localStorage)
    setQuickStart({ ...quickStart, destination: 'completed' });

    // Show completion toast
    const toastRef = toast({
      variant: 'success',
      title: t(
        'step_3_completed',
        'Google Drive connected! Moving to report creation...'
      ),
    });

    // Auto-dismiss toast after 3 seconds
    setTimeout(() => {
      toastRef.dismiss();
    }, 3000);

    // Track completion
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_ADD_DESTINATION_COMPLETED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );

    // Navigate after a delay
    setTimeout(() => {
      navigate(
        `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.REPORTING}/${APP_ROUTE.REPORTS}`
      );
    }, 2000);
  };

  const completeReportStep = async () => {
    if (!isActivationFlow) return;

    // Update state (atomWithStorage automatically saves to localStorage)
    setQuickStart({ ...quickStart, report: 'completed' });

    // Show completion toast
    const toastRef = toast({
      variant: 'success',
      title: t(
        'step_4_completed',
        'Report created! Activation flow completed! 🎉'
      ),
    });

    // Auto-dismiss toast after 3 seconds
    setTimeout(() => {
      toastRef.dismiss();
    }, 3000);

    // THIS IS THE FINAL STEP - call backend to set activationFlow = true
    try {
      await axios.patch(`/update-activation/${userState.business?.id}`, {
        activationFlow: true,
      });

      // Note: localStorage will persist the completed state
      // When activationFlow becomes true, the UI won't show the flow anymore
    } catch (error) {
      console.error('Failed to update activation flow:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to complete activation flow',
        description: 'Please try again or contact support.',
      });
    }

    // Track completion
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_CREATE_REPORT_COMPLETED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );
  };

  return {
    isActivationFlow,
    completeDataSourceStep,
    completeDestinationStep,
    completeReportStep,
    quickStart,
  };
};
